import { TypedDocumentNode, gql } from '@apollo/client';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { CardBack } from '@sorare/core/src/components/card/Back';
import { Fan } from '@sorare/core/src/components/rewards/Banner/Fan';
import { range } from '@sorare/core/src/lib/arrays';

import { InviteLinkBanner } from 'components/leagues/InviteLinkBanner';
import { GroupMembersList } from 'components/leagues/LeagueMembersList';
import useLeaveLeague from 'components/leagues/useLeaveLeague';

import { LeagueMembers_League } from './__generated__/index.graphql';

type Props = {
  league: LeagueMembers_League;
  sport: Sport;
  inviteRoute: string;
  afterLeaveRoute: string;
  isMember: boolean;
  loadMore: () => void;
};

export const LeagueMembers = ({
  league,
  sport,
  inviteRoute,
  afterLeaveRoute,
  isMember,
  loadMore,
}: Props) => {
  const navigate = useNavigate();
  const leaveGroupMutation = useLeaveLeague();

  const leaveGroup = useCallback(async () => {
    await leaveGroupMutation({
      leagueSlug: league.slug!,
    });
    navigate(afterLeaveRoute);
  }, [leaveGroupMutation, league.slug, navigate, afterLeaveRoute]);

  return (
    <>
      {isMember && (
        <InviteLinkBanner
          inviteRoute={inviteRoute}
          leagueSlug={league.slug}
          icon={
            <Fan
              elements={range(2).map(i => (
                <CardBack key={i} rarity={Rarity.limited} sport={Sport.NBA} />
              ))}
            />
          }
        />
      )}
      <GroupMembersList
        league={league}
        sport={sport}
        loadMore={loadMore}
        leaveGroup={isMember ? leaveGroup : undefined}
      />
    </>
  );
};

LeagueMembers.fragments = {
  League: gql`
    fragment LeagueMembers_League on League {
      slug
      ...GroupMembersList_League
    }
    ${GroupMembersList.fragments.League}
  ` as TypedDocumentNode<LeagueMembers_League>,
};
